import { useState, useEffect, useRef } from "react";
import { useLanguage } from "../context/LanguageContext";
import { track } from "../utils/analytics";

/* ─── Sramika brand colours (from logo) ────────────────────────────── */
const GREEN  = "#4CAF50";   // Signature green
const LIGHT_GREEN = "#77BF21"; // Lighter green for active state
const ACTIVE_BG   = "#F2F9EF"; // Light green background
const NAVY   = "#37474F";   // Dark grey-blue text
const GREY   = "#78909C";   // Lighter grey text
const GREEN_DEEP  = "#2E7D32"; // Readable green for text on white
const ORANGE_DEEP = "#E8750A"; // Logo car orange
const BLUE   = "#1F5FA8";   // Logo pin blue
const LINE   = "#E6ECEA";   // Hairlines and card borders

/* ─── Language list ────────────────────────────────────────────────── */
const LANGS = [
  { code: "en", native: "English",  label: "English",  abbr: "En" },
  { code: "te", native: "తెలుగు",   label: "Telugu",   abbr: "తె" },
  { code: "ta", native: "தமிழ்",    label: "Tamil",    abbr: "தம" },
  { code: "hi", native: "हिन्दी",    label: "Hindi",    abbr: "हि" },
  { code: "kn", native: "ಕನ್ನಡ",    label: "Kannada",  abbr: "ಕನ" },
];

const TOTAL = 2; // 0=language, 1=what brings you here
const AUTO_HOME_MS = 3000;
const LANG_AUTO_MS = 4000;

/* Photos on the two earning cards. Requested while the language list is showing, so
   they are already there when the chooser opens. */
const EARN_PHOTOS = {
  CAB_OWNER: "/onboarding/cab-owner.jpg",
  ALTING_DRIVER: "/onboarding/alting-driver.jpg",
};

export default function FirstLaunchExplainer({ onComplete }) {
  const [step, setStep] = useState(0);
  const { t, lang, setLang } = useLanguage();

  /* The four-second auto-advance and the slide preloader that used to sit here are
     gone with the slides they served. Neither was ever reached: step 0 completed
     the flow outright, so step 1 never rendered. */
  /* The chooser stays three seconds, then carries on to Home - most first visitors are
     customers, and the choice should not stand between them and the cars. The first
     touch anywhere stops the countdown, so someone reaching for "Join as Cab account"
     is never moved away mid-tap; they then choose by tapping, as before. */
  const [autoHome, setAutoHome] = useState(false);
  const autoTimer = useRef(null);
  const holdTimer = () => {
    if (autoTimer.current) { clearTimeout(autoTimer.current); autoTimer.current = null; }
    setAutoHome(false);
  };
  useEffect(() => {
    if (step !== 1) return undefined;
    const start = setTimeout(() => setAutoHome(true), 30); // lets the bar animate from 0
    autoTimer.current = setTimeout(() => {
      autoTimer.current = null;
      track("first_open_intent", { intent: "AUTO_HOME" });
      onComplete(null);
    }, AUTO_HOME_MS);
    return () => { clearTimeout(start); if (autoTimer.current) clearTimeout(autoTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  /* The language list stays four seconds, then moves on to the chooser with the
     language already highlighted - most visitors keep the default, and should not
     have to tap to get past it. Same rules as the chooser's countdown: the bar shows
     it, and the first touch stops it. The bar is reset as the step changes, so the
     chooser's own countdown starts from empty. */
  useEffect(() => {
    if (step !== 0) return undefined;
    Object.values(EARN_PHOTOS).forEach((src) => { const i = new Image(); i.src = src; });
    const start = setTimeout(() => setAutoHome(true), 30);
    autoTimer.current = setTimeout(() => {
      autoTimer.current = null;
      setAutoHome(false);
      setStep(1);
    }, LANG_AUTO_MS);
    return () => { clearTimeout(start); if (autoTimer.current) clearTimeout(autoTimer.current); };
  }, [step]);

  const goNext = () => (step < TOTAL - 1 ? setStep(step + 1) : onComplete());
  const goBack = () => step > 0 ? setStep(step - 1) : null;

  /* ═══════════════════════════════════════════════════════════════════
     STEP 0 — Language Selection Page  (matches 2nd reference screenshot)
  ═══════════════════════════════════════════════════════════════════ */
  if (step === 0) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col bg-white select-none overflow-hidden"
           onTouchStart={holdTimer} onMouseDown={holdTimer}
           style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>

        {/* The 4-second countdown to the next screen; stops at the first touch. */}
        <div className="h-[3px] w-full bg-[#E6F0E3] flex-shrink-0" aria-hidden="true">
          <div className="h-full bg-[#7BAE00]"
               style={{ width: autoHome ? "100%" : "0%", transition: autoHome ? `width ${LANG_AUTO_MS}ms linear` : "none" }} />
        </div>

        {/* ── Welcome: the real logo and live text ──────────────────────
            Was one 576px JPEG with the logo and every line of text baked into it,
            stretched to the screen width - soft on every phone, and blurrier the
            sharper the screen. Now the logo is the 512px mark and the words are
            text, so both are crisp at any size. */}
        <div className="w-full flex-shrink-0 relative overflow-hidden text-center px-6 pt-7 pb-5"
             style={{ background: "linear-gradient(180deg, #E8F4FC 0%, #F1F8F1 55%, #FFFFFF 100%)" }}>
          <div className="flex items-center justify-center gap-3">
            <img src="/logo-160.png" alt="" width="64" height="64"
                 className="w-16 h-16 rounded-full bg-white object-contain"
                 style={{ boxShadow: "0 6px 18px rgba(76,175,80,0.22)" }} />
            <div className="text-left">
              <div className="text-[30px] font-extrabold leading-none tracking-tight" style={{ color: GREEN }}>{t("lg_brand")}</div>
              {/* Once a language is tapped, the line under the name is in it too. */}
              <div className="text-[15px] font-semibold mt-1 leading-snug" style={{ color: "#5B6B8C" }}>
                {lang === "en"
                  ? <><span className="whitespace-nowrap">Nearby Car Travels</span><br /><span className="whitespace-nowrap">&amp; Alt Drivers</span></>
                  : t("onb_intentTagline")}
              </div>
            </div>
          </div>

          {/* Dotted rule with the car in the middle, as in the original artwork. */}
          <div className="flex items-center gap-2 mt-5 max-w-[300px] mx-auto" aria-hidden="true">
            <span className="flex-1 border-t-2 border-dotted" style={{ borderColor: "#A5D6A7" }} />
            <svg width="30" height="20" viewBox="0 0 30 20" fill="none" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 13V9.5l2.6-4.6A2 2 0 0 1 8.3 4h13.4a2 2 0 0 1 1.7 .9L26 9.5V13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
              <path d="M4.5 9.5h21" /><circle cx="9" cy="14" r="2" fill="white" /><circle cx="21" cy="14" r="2" fill="white" />
            </svg>
            <span className="flex-1 border-t-2 border-dotted" style={{ borderColor: "#A5D6A7" }} />
          </div>

          <p className="mt-3 text-[10px] font-semibold tracking-[0.06em] uppercase [text-wrap:balance]" style={{ color: "#3F5A7A" }}>
            Choose the best cabs &amp; drivers near you
          </p>
          <h1 className="mt-2 text-[24px] font-extrabold leading-tight" style={{ color: "#1C3F94" }}>
            Welcome to Sramika
          </h1>
          <p className="mt-1 text-[14px] font-semibold" style={{ color: GREEN_DEEP }}>
            {t("onb_lang_title")}
          </p>
        </div>

        {/* ── Language list (scrollable) ─────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          <div className="max-w-sm mx-auto flex flex-col gap-2">
            {LANGS.map((l) => {
              const active = lang === l.code;
              return (
                <button
                  key={l.code}
                  onClick={() => {
                    setLang(l.code);
                    /* Was onComplete() - which is why the rest of this component has
                       never rendered. The short delay lets the radio fill before the
                       screen changes. */
                    setTimeout(() => goNext(), 250);
                  }}
                  className="w-full flex items-center justify-between text-left transition-all duration-150 active:scale-[0.98]"
                  style={{
                    padding: "8px 12px",
                    borderRadius: "10px",
                    border: active ? `1.5px solid ${LIGHT_GREEN}` : "1.5px solid #E0E0E0",
                    background: active ? ACTIVE_BG : "white",
                    boxShadow: active ? `0 2px 10px ${LIGHT_GREEN}20` : "0 1px 3px rgba(0,0,0,0.04)"
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-black flex-shrink-0"
                         style={{
                           background: active ? `${LIGHT_GREEN}30` : "#F5F5F5",
                           color: active ? LIGHT_GREEN : NAVY
                         }}>
                      {l.abbr}
                    </div>
                    <div>
                      <div className="text-[13px] font-bold" style={{ color: active ? LIGHT_GREEN : NAVY }}>
                        {l.native}
                      </div>
                      <div className="text-[9px] font-medium" style={{ color: active ? LIGHT_GREEN : GREY }}>
                        {l.label}
                      </div>
                    </div>
                  </div>
                  {/* Radio circle */}
                  <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                       style={{
                         border: active ? `2px solid ${LIGHT_GREEN}` : "1.5px solid #BDBDBD",
                         background: "transparent"
                       }}>
                    {active && <div className="w-2 h-2 rounded-full" style={{ background: LIGHT_GREEN }} />}
                  </div>
                </button>
              );
            })}

            {/* More Languages */}
            <button className="w-full flex items-center gap-3 text-left"
                    style={{ padding: "8px 12px", borderRadius: "10px", border: "1.5px solid #E0E0E0", background: "white" }}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[13px] font-black flex-shrink-0"
                   style={{ background: "#F5F5F5", color: GREY }}>+</div>
              <div>
                <div className="text-[13px] font-bold" style={{ color: NAVY }}>More Languages</div>
                <div className="text-[9px] font-medium" style={{ color: GREY }}>Select other languages</div>
              </div>
              <div className="ml-auto w-4 h-4 rounded-full flex-shrink-0"
                   style={{ border: "1.5px solid #BDBDBD" }} />
            </button>
          </div>
        </div>

        {/* ── Bottom CTA + dots ──────────────────────────────────────── */}
        <div className="flex-shrink-0 px-5 pb-4 pt-1">
          <div className="max-w-sm mx-auto flex flex-col items-center gap-1.5">
            <button onClick={onComplete}
                    className="text-xs font-semibold py-1.5" style={{ color: GREY }}>
              Skip Intro
            </button>
            {/* Pagination dots */}
            <div className="flex items-center gap-1.5 mt-1">
              {Array.from({ length: TOTAL }).map((_, i) => (
                <div key={i} style={{
                  width: i === step ? "18px" : "7px",
                  height: "7px",
                  borderRadius: "999px",
                  background: i === step ? LIGHT_GREEN : "transparent",
                  border: i === step ? "none" : "1.5px solid #CFD8DC",
                  transition: "all 0.3s"
                }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════════
     STEP 1 — What brings you here

     Every string here has a key in all five languages. t() returns the key itself
     when one is missing, so a `|| "fallback"` never fires - a new key without
     translations shows up on screen as "onb_somethingTitle".
  ═══════════════════════════════════════════════════════════════════ */
  const choose = (intent, path) => {
    /* track() is fire-and-forget and swallows its own errors; we do not await it,
       because a slow network must not sit between a tap and the next screen. */
    track("first_open_intent", { intent });
    onComplete(path);
  };

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  /* Letter-spacing pulls apart the joined letters of Telugu, Tamil, Hindi and
     Kannada, so the small spaced capitals are for English only. */
  const caps = lang === "en" ? "text-[10.5px] uppercase tracking-[0.14em]" : "text-[12px]";

  /* The two ways to earn lead to a form that wants an RC book, a licence and four
     photographs, so they sit under their own heading rather than beside "have a look". */
  const earn = [
    { intent: "CAB_OWNER", path: "/cab-owner/onboarding",
      title: t("onb_intentCab"), sub: t("onb_intentCabSub"),
      accent: ORANGE_DEEP, tint: "#FFF4E5", photo: EARN_PHOTOS.CAB_OWNER },
    { intent: "ALTING_DRIVER", path: "/alting-driver/onboarding",
      title: t("onb_intentDriver"), sub: t("onb_intentDriverSub"),
      accent: BLUE, tint: "#EAF2FB", photo: EARN_PHOTOS.ALTING_DRIVER },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex flex-col select-none overflow-hidden"
         onTouchStart={holdTimer} onMouseDown={holdTimer}
         style={{ fontFamily: "'Inter','Segoe UI',sans-serif",
                  background: "linear-gradient(180deg, #EEF7EA 0%, #F7FBF5 30%, #FFFFFF 55%)" }}>

      {/* The 3-second countdown to Home, visible so the move is not a surprise. It
          stops at the first touch - see holdTimer. */}
      <div className="h-[3px] w-full bg-[#E6F0E3] flex-shrink-0" aria-hidden="true">
        <div className="h-full bg-[#7BAE00]"
             style={{ width: autoHome ? "100%" : "0%", transition: autoHome ? `width ${AUTO_HOME_MS}ms linear` : "none" }} />
      </div>

      {/* Language pill - goes back to the list, and says which one is in use. */}
      <div className="flex justify-end px-4 pt-3 flex-shrink-0">
        <button onClick={goBack}
                aria-label={t("onb_language")}
                className="flex items-center gap-1.5 pl-2.5 pr-2 py-1.5 rounded-full bg-white active:scale-95 transition-all"
                style={{ border: `1px solid ${LINE}`, boxShadow: "0 1px 4px rgba(15,40,30,0.06)", color: NAVY }}>
          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" />
            <path strokeLinecap="round" d="M3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z" />
          </svg>
          <span className="text-[12px] font-semibold">{current.native}</span>
          <svg width="12" height="12" fill="none" stroke={GREY} strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>

      {/* Half the screen for customers, a quarter each for the two ways to earn - the
          split the business wants, in the same proportions on every phone. */}
      {/* A laptop (mock Laptop_2, change 9): the same three choices side by side. */}
      <div className="hidden lg:flex flex-1 min-h-0 flex-col items-center justify-center px-8 pb-16">
        <div className="flex items-center justify-center gap-3">
          <img src="/logo-mark.png" alt="" className="w-14 h-14 rounded-full bg-white object-contain"
               style={{ boxShadow: "0 4px 14px rgba(76,175,80,0.20)" }} />
          <div className="text-left">
            <div className="text-[28px] font-extrabold tracking-tight leading-none" style={{ color: GREEN_DEEP }}>{t("lg_brand")}</div>
            <div className={`mt-1.5 font-semibold ${caps}`} style={{ color: GREY }}>{t("onb_intentTagline")}</div>
          </div>
        </div>
        <h1 className="text-center text-[30px] font-bold leading-snug mt-6" style={{ color: NAVY }}>{t("onb_intentTitle")}</h1>
        <div className="grid grid-cols-3 gap-6 mt-8 w-full max-w-[1080px]">
          <button onClick={() => choose("CUSTOMER", null)}
                  className="text-left bg-white hover:-translate-y-0.5 transition-all flex flex-col p-7"
                  style={{ borderRadius: "22px", border: `1.5px solid ${GREEN}`, boxShadow: "0 10px 28px rgba(76,175,80,0.16)" }}>
            <div className="h-[170px] rounded-2xl flex items-center justify-center" style={{ background: "#E8F5E9", color: GREEN_DEEP }}>
              <div style={{ transform: "scale(3.4)" }}><CarFrontIcon /></div>
            </div>
            <div className="mt-5 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-extrabold text-[20px] leading-tight" style={{ color: NAVY }}>{t("onb_intentRide")}</div>
                <div className="text-[14px] mt-1.5 leading-snug" style={{ color: GREY }}>{t("onb_intentRideSub")}</div>
              </div>
              <Chevron bg={GREEN} color="white" />
            </div>
          </button>
          {earn.map((o) => (
            <button key={o.intent} onClick={() => choose(o.intent, o.path)}
                    className="text-left bg-white hover:-translate-y-0.5 transition-all flex flex-col p-7"
                    style={{ borderRadius: "22px", border: `1px solid ${LINE}`, boxShadow: "0 4px 14px rgba(15,40,30,0.05)" }}>
              <div className="h-[170px] rounded-2xl overflow-hidden relative" style={{ background: o.tint }}>
                <img src={o.photo} alt="" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="mt-5 flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[20px] leading-tight" style={{ color: NAVY }}>{o.title}</div>
                  <div className="text-[14px] mt-1.5 leading-snug" style={{ color: GREY }}>{o.sub}</div>
                </div>
                <Chevron bg={o.tint} color={o.accent} />
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:hidden flex-1 min-h-0 flex flex-col w-full max-w-md mx-auto px-5 pb-5">

        {/* ── Customers: the top half ─────────────────────────────── */}
        <div className="flex-[2] min-h-0 flex flex-col">
          <div className="flex items-center justify-center gap-2.5 pt-1">
            <img src="/logo-mark.png" alt="" className="w-10 h-10 rounded-full bg-white object-contain"
                 style={{ boxShadow: "0 4px 14px rgba(76,175,80,0.20)" }} />
            <div className="text-left">
              <div className="text-[20px] font-extrabold tracking-tight leading-none" style={{ color: GREEN_DEEP }}>{t("lg_brand")}</div>
              <div className={`mt-1 font-semibold ${caps}`} style={{ color: GREY }}>{t("onb_intentTagline")}</div>
            </div>
          </div>

          <h1 className="text-center text-[19px] font-bold leading-snug mt-3" style={{ color: NAVY }}>
            {t("onb_intentTitle")}
          </h1>

          <button onClick={() => choose("CUSTOMER", null)}
                  className="mt-3 flex-1 min-h-0 w-full text-left bg-white active:scale-[0.99] transition-all flex flex-col justify-center"
                  style={{ padding: "18px", borderRadius: "22px",
                           border: `1.5px solid ${GREEN}`, boxShadow: "0 10px 28px rgba(76,175,80,0.16)" }}>
            <div className="flex items-center gap-3">
              <IconTile tint="#E8F5E9" accent={GREEN_DEEP}><CarFrontIcon /></IconTile>
              <div className="min-w-0 flex-1">
                <div className="font-extrabold text-[17px] leading-tight" style={{ color: NAVY }}>{t("onb_intentRide")}</div>
                <div className="text-[12.5px] mt-1 leading-snug" style={{ color: GREY }}>{t("onb_intentRideSub")}</div>
              </div>
              <Chevron bg={GREEN} color="white" />
            </div>
          </button>
        </div>

        {/* ── Earning: the bottom half, a quarter each ────────────── */}
        <div className="flex-[2] min-h-0 flex flex-col mt-4">
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="flex-1 h-px" style={{ background: LINE }} />
            <span className="text-[13px] font-bold text-center" style={{ color: NAVY }}>{t("onb_intentEarn")}</span>
            <span className="flex-1 h-px" style={{ background: LINE }} />
          </div>

          <div className="flex-1 min-h-0 flex flex-col gap-2.5 mt-3">
            {earn.map((o) => (
              <button key={o.intent} onClick={() => choose(o.intent, o.path)}
                      className="flex-1 min-h-0 w-full text-left bg-white active:scale-[0.99] transition-all flex items-stretch overflow-hidden"
                      style={{ borderRadius: "18px", border: `1px solid ${LINE}`,
                               boxShadow: "0 4px 14px rgba(15,40,30,0.05)" }}>
                {/* A real photo of the work - a cab, a driver at the wheel - in place of
                    the icon: it says "this is a job" faster than a line of text. The
                    tint shows while it loads, so the card never flashes empty. */}
                <div className="w-[36%] shrink-0 relative" style={{ background: o.tint }}>
                  <img src={o.photo} alt="" decoding="async"
                       className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <div className="flex items-center gap-3 flex-1 min-w-0 p-3.5">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[15px] leading-tight" style={{ color: NAVY }}>{o.title}</div>
                    <div className="text-[12px] mt-1 leading-snug" style={{ color: GREY }}>{o.sub}</div>
                  </div>
                  <Chevron bg={o.tint} color={o.accent} />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Small pieces for the intent screen ───────────────────────────── */
function IconTile({ tint, accent, children }) {
  return (
    <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
         style={{ background: tint, color: accent }}>
      {children}
    </div>
  );
}

function Chevron({ bg, color }) {
  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
      <svg width="15" height="15" fill="none" stroke={color} strokeWidth="2.6" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
      </svg>
    </div>
  );
}

const iconProps = { width: 24, height: 24, fill: "none", stroke: "currentColor", strokeWidth: 1.8,
                    strokeLinecap: "round", strokeLinejoin: "round", viewBox: "0 0 24 24" };

function CarFrontIcon() {
  return (
    <svg {...iconProps}>
      <path d="M5 11l1.6-4.2A2 2 0 018.5 5.5h7a2 2 0 011.9 1.3L19 11" />
      <rect x="3" y="11" width="18" height="6.5" rx="2" />
      <path d="M5.5 17.5v2M18.5 17.5v2M3 9.5l2 1.5M21 9.5l-2 1.5" />
      <circle cx="7.5" cy="14.2" r="1" fill="currentColor" stroke="none" />
      <circle cx="16.5" cy="14.2" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

